import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Search, ShieldCheck, ShieldAlert, ShieldQuestion, Drama, Trash2,
  ExternalLink, History, Loader2, CheckCircle2, XCircle, HelpCircle, Youtube,
  LogIn, LogOut, Coins, X, Sparkles, Share2, Copy, Check, Link as LinkIcon,
  ArrowLeft, BarChart3, FileText, Users, TrendingUp, Puzzle, Download, Image as ImageIcon,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TODO: after the extension is approved, replace the search URLs with the direct store-listing URLs.
const EXTENSION_URLS = {
  chrome: "https://chromewebstore.google.com/detail/factscope-%E2%80%94-youtube-fake/hhngbkneopoleklbifnilmbenokkcpfm",
  firefox: "https://addons.mozilla.org/en-US/firefox/addon/factscope/",
};
function getExtensionCTA() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/Firefox\//i.test(ua)) return { label: "Add to Firefox", url: EXTENSION_URLS.firefox };
  if (/Edg\//i.test(ua)) return { label: "Add to Edge", url: EXTENSION_URLS.chrome };
  if (/OPR\//i.test(ua)) return { label: "Add to Opera", url: EXTENSION_URLS.chrome };
  return { label: "Add to Chrome", url: EXTENSION_URLS.chrome };
}
const EXT_CTA = getExtensionCTA();

const CLIENT_ID = (() => {
  let id = localStorage.getItem("factscope_cid");
  if (!id) {
    id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now()) + Math.random();
    localStorage.setItem("factscope_cid", id);
  }
  return id;
})();

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "X-Client-Id": CLIENT_ID },
});

const VERDICTS = {
  Real: { bg: "#E8F5E9", text: "#1B5E20", border: "#4CAF50", Icon: ShieldCheck, label: "LIKELY REAL" },
  Fake: { bg: "#FFEBEE", text: "#B71C1C", border: "#F44336", Icon: ShieldAlert, label: "LIKELY FAKE" },
  Mixed: { bg: "#FFF8E1", text: "#F57F17", border: "#FFC107", Icon: ShieldQuestion, label: "MIXED / UNVERIFIED" },
  Story: { bg: "#F3E5F5", text: "#4A148C", border: "#9C27B0", Icon: Drama, label: "STORY / SATIRE" },
};
const vstyle = (v) => VERDICTS[v] || VERDICTS.Mixed;

const ASSESS = {
  Supported: { Icon: CheckCircle2, color: "#1B5E20" },
  Disputed: { Icon: XCircle, color: "#B71C1C" },
  Unverified: { Icon: HelpCircle, color: "#F57F17" },
};

function ConfidenceMeter({ value, color = "#606060" }) {
  return (
    <div data-testid="confidence-meter">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-[#606060] uppercase tracking-wide">Confidence</span>
        <span className="text-sm font-bold" style={{ color }}>{value}%</span>
      </div>
      <div className="w-full h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

function VerdictCard({ r }) {
  const s = vstyle(r.verdict);
  const Icon = s.Icon;
  return (
    <div data-testid="verdict-card" className="rounded-xl p-5 border" style={{ background: s.bg, borderColor: s.border }}>
      <div className="flex items-center gap-3">
        <div className="rounded-full p-2.5 shrink-0" style={{ background: s.border }}>
          <Icon size={24} color="#fff" strokeWidth={2.4} />
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide" style={{ color: s.text }}>Verdict</div>
          <div className="font-display font-black text-2xl sm:text-3xl tracking-tight" style={{ color: s.text }}>{s.label}</div>
        </div>
      </div>
      <div className="mt-4"><ConfidenceMeter value={r.confidence} color={s.border} /></div>
      <p className="mt-4 text-sm sm:text-base leading-relaxed" style={{ color: s.text }} data-testid="verdict-summary">{r.summary}</p>
      {r.category && (
        <span className="mt-4 inline-block rounded-lg px-2.5 py-1 text-xs font-medium"
          style={{ background: "#fff", border: `1px solid ${s.border}`, color: s.text }}>
          {r.category}{!r.transcript_available && " · no transcript"}
        </span>
      )}
    </div>
  );
}

function DeepCheckNotice() {
  return (
    <div className="rounded-xl border border-[#FFE0A6] bg-[#FFF9EC] p-4" data-testid="deep-check-notice">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-[#B7791F] shrink-0"><FileText size={18} /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#8a5a00]">Tip: this was a quick title-only check</p>
          <p className="mt-1 text-xs text-[#8a5a00] leading-relaxed">
            YouTube blocks our server from reading transcripts, so this verdict is based on the title alone. For a more comprehensive, extensive result, open the video's transcript on YouTube and paste it into the optional transcript box <strong>before</strong> analyzing — or use the free browser extension, which reads it automatically.
          </p>
        </div>
      </div>
    </div>
  );
}

function ResultPanel({ r, onShare, shared }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="space-y-6" data-testid="result-panel"
    >
      {shared && (
        <div className="rounded-xl bg-[#0F0F0F] text-white px-4 py-3 flex items-center justify-between" data-testid="shared-banner">
          <span className="text-xs font-medium tracking-wide">SHARED VERDICT · read-only</span>
          <a href={window.location.origin + "/"} className="text-xs font-semibold text-[#FF4444] hover:underline">Check your own video →</a>
        </div>
      )}
      <div className="flex gap-4 rounded-xl border border-[#E5E5E5] bg-white p-4">
        {r.thumbnail && (
          <img src={r.thumbnail} alt="" className="w-32 h-auto object-cover rounded-lg border border-[#E5E5E5]" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-[#909090]">{r.channel || "YouTube"}</div>
          <h3 className="font-display font-bold text-xl tracking-tight text-[#0F0F0F] line-clamp-2">{r.title || r.video_url}</h3>
          <div className="mt-1.5 flex items-center gap-4">
            <a href={r.video_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#606060] hover:text-[#0F0F0F]"
              data-testid="watch-link">
              <Youtube size={15} className="text-[#FF0000]" /> Watch on YouTube <ExternalLink size={12} />
            </a>
            {!shared && onShare && (
              <button onClick={() => onShare(r)} data-testid="share-button"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#065FD4] hover:underline">
                <Share2 size={14} /> Share verdict
              </button>
            )}
          </div>
        </div>
      </div>

      <VerdictCard r={r} />

      {!shared && !r.transcript_available && <DeepCheckNotice />}

      <div className="rounded-xl border border-[#E5E5E5] bg-white p-5">
        <h4 className="font-display text-base font-bold text-[#0F0F0F]">Reasoning</h4>
        <p className="mt-2 text-base leading-relaxed text-[#333]" data-testid="reasoning-text">{r.reasoning}</p>
      </div>

      {r.claims?.length > 0 && (
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-5">
          <h4 className="font-display text-base font-bold text-[#0F0F0F] mb-2">
            Flagged Claims ({r.claims.length})
          </h4>
          <Accordion type="single" collapsible className="w-full">
            {r.claims.map((c, i) => {
              const a = ASSESS[c.assessment] || ASSESS.Unverified;
              const AIcon = a.Icon;
              return (
                <AccordionItem value={`c${i}`} key={c.claim || `claim-${i}`} className="border-[#E5E5E5]">
                  <AccordionTrigger className="text-left hover:no-underline" data-testid={`claim-trigger-${i}`}>
                    <span className="flex items-start gap-2 pr-2">
                      <AIcon size={18} style={{ color: a.color }} className="mt-0.5 shrink-0" />
                      <span className="text-sm font-medium text-[#0F0F0F]">{c.claim}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-7">
                      <span className="font-mono-plex text-xs uppercase tracking-wider" style={{ color: a.color }}>
                        {c.assessment}
                      </span>
                      <p className="mt-1 text-sm text-[#333] leading-relaxed">{c.detail}</p>
                      {c.sources?.map((s, j) => (
                        <a key={j} href={s.url} target="_blank" rel="noreferrer"
                          className="mt-2 flex items-center gap-1 text-xs text-[#065FD4] hover:underline">
                          <ExternalLink size={12} /> {s.title || s.url}
                        </a>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}

      {r.sources?.length > 0 && (
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-5">
          <h4 className="font-display text-base font-bold text-[#0F0F0F] mb-2">Sources</h4>
          <ul className="space-y-2">
            {r.sources.map((s, i) => (
              <li key={s.url || `source-${i}`}>
                <a href={s.url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-[#065FD4] hover:underline" data-testid={`source-${i}`}>
                  <ExternalLink size={14} className="shrink-0" /> {s.title || s.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}

function LoadingState() {
  const logs = ["fetching transcript...", "extracting claims...", "searching the web...", "cross-checking sources...", "rendering verdict..."];
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const EST = 30; // typical seconds
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % logs.length), 1800);
    const s = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { clearInterval(t); clearInterval(s); };
  }, []);
  const pct = Math.min(96, Math.round((1 - Math.exp(-elapsed / EST)) * 100));
  const remaining = Math.max(0, EST - elapsed);
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-8 tracing-beam" data-testid="loading-state">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin text-[#FF0000]" size={24} />
          <span className="font-display font-black text-2xl tracking-tight">Analyzing…</span>
        </div>
        <span className="font-mono-plex text-sm font-bold text-[#606060] tabular-nums" data-testid="loading-timer">{elapsed}s</span>
      </div>
      <div className="mt-4 h-2 rounded-full bg-[#F0F0F0] overflow-hidden">
        <div className="h-full bg-[#FF0000] rounded-full transition-[width] duration-1000 ease-out" style={{ width: `${pct}%` }} data-testid="loading-progress" />
      </div>
      <p className="mt-3 text-sm text-[#606060]">{logs[idx]}</p>
      <p className="mt-1 text-xs text-[#909090]" data-testid="loading-estimate">
        {remaining > 0 ? `about ${remaining}s left · usually 15–40s (longer videos take more)` : "almost done · finishing up…"}
      </p>
    </div>
  );
}

function Paywall({ mode, user, onClose, onBuy, buying }) {
  const heading = mode === "free_limit_reached" ? "You've used your 2 free checks"
    : mode === "needs_credits" ? "You're out of credits"
    : "Get more checks";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="paywall-modal">
      <div className="bg-white rounded-2xl border border-[#E5E5E5] shadow-2xl max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-[#999] hover:text-black" data-testid="paywall-close"><X size={20} /></button>
        <div className="bg-[#FF0000] text-white w-12 h-12 rounded-xl flex items-center justify-center mb-4"><Sparkles size={24} /></div>
        <h3 className="font-display font-black text-2xl tracking-tight">{heading}</h3>
        <p className="mt-2 text-[#606060] text-sm leading-relaxed">
          Unlock unlimited fact-checks. Get 100 checks for $5 — credits never expire, and repeat checks of the same video are always free.
        </p>
        <div className="mt-5 rounded-xl border border-[#E5E5E5] bg-[#F8F8F8] p-4 flex items-center justify-between">
          <div>
            <div className="font-display font-black text-xl">100 checks</div>
            <div className="text-xs text-[#909090]">credits never expire</div>
          </div>
          <div className="font-display font-black text-3xl">$5</div>
        </div>
        <button onClick={onBuy} disabled={buying} data-testid="checkout-button"
          className="mt-4 w-full h-12 bg-[#FF0000] text-white font-medium rounded-full hover:bg-[#CC0000] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          {buying ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
          {user ? "Buy 100 checks — $5" : "Sign in to buy"}
        </button>
        <p className="mt-3 text-center text-[11px] text-[#909090]">Secure checkout via Stripe.</p>
      </div>
    </div>
  );
}

function ShareModal({ share, onClose }) {
  const [copied, setCopied] = useState("");
  const badgeUrl = `${API}/badge/${share.id}`;
  const cardUrl = `${API}/card/${share.id}.png`;
  const md = `[![FactScope verdict](${badgeUrl})](${share.url})`;
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };
  const downloadCard = async () => {
    try {
      const resp = await fetch(cardUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factscope-${(share.verdict || "verdict").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Image saved — post it on Instagram, Facebook or WhatsApp!");
    } catch (e) {
      toast.error("Couldn't download the image, please try again");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="share-modal">
      <div className="bg-white rounded-2xl border border-[#E5E5E5] shadow-2xl max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-[#999] hover:text-black" data-testid="share-close"><X size={20} /></button>
        <div className="bg-[#0A0A0A] text-white w-12 h-12 flex items-center justify-center mb-4"><Share2 size={22} /></div>
        <h3 className="font-display font-black text-2xl tracking-tight">Share this verdict</h3>
        <p className="mt-1 text-[#555] text-sm">Anyone with the link sees a read-only verdict — and a nudge to check their own videos.</p>

        <div className="mt-5" data-testid="share-image-section">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#999] mb-2 flex items-center gap-1.5"><ImageIcon size={12} /> Post-worthy image</div>
          <div className="rounded-xl overflow-hidden border border-[#E5E5E5] bg-[#0A0A0A]">
            <img src={cardUrl} alt="FactScope verdict card" data-testid="share-card-preview" className="w-full block" />
          </div>
          <button onClick={downloadCard} data-testid="download-card-button"
            className="mt-2 w-full h-11 bg-[#FF0000] text-white rounded-full flex items-center justify-center gap-2 text-sm font-semibold hover:bg-[#cc0000] transition-colors">
            <Download size={16} /> Download image to post
          </button>
          <div className="flex gap-2 mt-2">
            <a href={`https://wa.me/?text=${encodeURIComponent("I fact-checked this YouTube video with FactScope: " + share.verdict + " (" + share.confidence + "% confidence) " + share.url)}`}
              target="_blank" rel="noreferrer" data-testid="share-whatsapp"
              className="flex-1 h-10 rounded-full border border-[#E5E5E5] flex items-center justify-center gap-2 text-xs font-medium hover:bg-[#F0F0F0] transition-colors">
              WhatsApp
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(share.url)}`}
              target="_blank" rel="noreferrer" data-testid="share-facebook"
              className="flex-1 h-10 rounded-full border border-[#E5E5E5] flex items-center justify-center gap-2 text-xs font-medium hover:bg-[#F0F0F0] transition-colors">
              Facebook
            </a>
          </div>
          <p className="mt-2 text-[11px] text-[#909090] leading-snug">Tip: download the image and post it — Instagram &amp; TikTok don't show link previews, but an image stops the scroll.</p>
        </div>

        <div className="mt-5">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#999] mb-1">Share link</div>
          <div className="flex gap-2">
            <input readOnly value={share.url} data-testid="share-link-input"
              className="flex-1 h-10 px-3 rounded-lg border border-[#E5E5E5] bg-[#F8F8F8] font-mono-plex text-xs focus:outline-none" />
            <button onClick={() => copy(share.url, "link")} data-testid="copy-link-button"
              className="h-10 px-3 bg-[#0F0F0F] text-white rounded-full flex items-center gap-1 text-xs font-medium hover:bg-[#272727]">
              {copied === "link" ? <Check size={14} /> : <Copy size={14} />} Copy
            </button>
          </div>
        </div>

        <div className="mt-5">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#999] mb-2">Embeddable badge</div>
          <div className="border border-[#E5E5E5] p-3 bg-[#FAFAFA] flex items-center justify-center">
            <img src={badgeUrl} alt="FactScope verdict badge" data-testid="badge-preview" />
          </div>
          <div className="flex gap-2 mt-2">
            <input readOnly value={md}
              className="flex-1 h-10 px-3 rounded-lg border border-[#E5E5E5] bg-[#F8F8F8] font-mono-plex text-[11px] focus:outline-none" />
            <button onClick={() => copy(md, "md")} data-testid="copy-badge-button"
              className="h-10 px-3 bg-[#0F0F0F] text-white rounded-full flex items-center gap-1 text-xs font-medium hover:bg-[#272727]">
              {copied === "md" ? <Check size={14} /> : <Copy size={14} />} MD
            </button>
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("I fact-checked this YouTube video with FactScope: " + share.verdict + " (" + share.confidence + "% confidence)")}&url=${encodeURIComponent(share.url)}`}
            target="_blank" rel="noreferrer" data-testid="share-twitter"
            className="flex-1 h-10 rounded-full border border-[#0F0F0F] flex items-center justify-center gap-2 text-xs font-medium hover:bg-[#0F0F0F] hover:text-white transition-colors">
            <LinkIcon size={14} /> Post to X
          </a>
        </div>
      </div>
    </div>
  );
}

const PRIVACY_CONTACT = "AppCappMine@Proton.me"; // contact email

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <header className="border-b border-[#E5E5E5] bg-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <a href="/" className="flex items-center gap-2 text-[#606060] hover:text-[#0F0F0F]" data-testid="privacy-back"><ArrowLeft size={18} /> Back</a>
          <div className="flex items-center gap-2 ml-2">
            <div className="bg-[#FF0000] text-white p-1.5 rounded-xl"><ShieldCheck size={20} /></div>
            <span className="font-display font-black text-lg">FactScope</span>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="privacy-page">
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-sm text-[#909090]">Last updated: June 2026</p>
        <div className="mt-6 space-y-5 text-[#3c3c3c] text-sm leading-relaxed [&_h2]:font-display [&_h2]:font-bold [&_h2]:text-lg [&_h2]:text-[#0F0F0F] [&_h2]:mt-6">
          <p>FactScope ("we", "the service") analyzes YouTube videos for potential misinformation. This policy explains what we collect and how we use it. FactScope is an independent tool and is not affiliated with, endorsed by, or sponsored by YouTube or Google.</p>
          <h2>What we collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Waitlist email</strong> — only if you choose to submit it, so we can notify you about paid plans.</li>
            <li><strong>Google account basics</strong> — if you sign in with Google we receive your name, email and profile picture (we never see your password).</li>
            <li><strong>Anonymous usage ID</strong> — a random identifier stored in your browser to count your free checks.</li>
            <li><strong>Content you submit</strong> — the YouTube links/titles you analyze.</li>
            <li><strong>Limited technical data</strong> — your IP address, used only to prevent abuse and rate-limit free usage.</li>
          </ul>
          <h2>How we use it</h2>
          <p>To provide and improve the analysis service, prevent abuse, and (only if you opted in) email you about launch news. We do <strong>not</strong> sell your data or use it for advertising.</p>
          <h2>Who we share it with</h2>
          <p>To produce a verdict we send the video's text to AI and web-search providers that process it on our behalf, and we use Google solely to authenticate sign-ins. We share data only as needed to run the service or to comply with the law.</p>
          <h2>Data retention &amp; your rights</h2>
          <p>We keep data only as long as needed for the service. You can request access to or deletion of your data at any time by emailing us at <strong>{PRIVACY_CONTACT}</strong>.</p>
          <h2>Cookies &amp; storage</h2>
          <p>We use a secure session cookie to keep you signed in, and your browser's local storage to remember your anonymous usage ID. No third-party advertising cookies.</p>
          <h2>Children</h2>
          <p>FactScope is not directed to children under 13.</p>
          <h2>Disclaimer</h2>
          <p>Verdicts are automated AI estimates and can be wrong. They are not professional fact-checking or legal advice. We accept no liability for decisions made based on the results.</p>
          <h2>Changes &amp; contact</h2>
          <p>We may update this policy; material changes will be posted here. Questions? Email <strong>{PRIVACY_CONTACT}</strong>.</p>
        </div>
      </main>
    </div>
  );
}

function StatBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-5">
      <div className="flex items-center gap-2 text-[#606060]"><Icon size={16} /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
      <div className="mt-2 font-display font-black text-3xl">{value}</div>
    </div>
  );
}

function StatsDashboard() {
  const [token, setToken] = useState(localStorage.getItem("fs_admin_token") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (t) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/stats`, { params: { token: t } });
      setData(res.data);
      localStorage.setItem("fs_admin_token", t);
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Wrong admin token." : "Could not load stats.");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (token) load(token); /* eslint-disable-next-line */ }, []);
  const maxDay = data ? Math.max(1, ...data.daily.map((d) => d.count)) : 1;
  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <header className="border-b border-[#E5E5E5] bg-white sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <a href="/" className="flex items-center gap-2 text-[#606060] hover:text-[#0F0F0F]"><ArrowLeft size={18} /> Back</a>
          <div className="flex items-center gap-2 ml-2">
            <div className="bg-[#FF0000] text-white p-1.5 rounded-xl"><BarChart3 size={20} /></div>
            <span className="font-display font-black text-lg">FactScope · Usage</span>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="stats-page">
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token"
            className="flex-1 h-11 px-4 border border-[#CCCCCC] rounded-full text-sm focus:outline-none focus:border-[#065FD4]" data-testid="admin-token-input" />
          <button onClick={() => load(token)} disabled={loading} data-testid="stats-load-button"
            className="h-11 px-6 bg-[#FF0000] text-white rounded-full font-medium hover:bg-[#CC0000] disabled:opacity-50">
            {loading ? "Loading…" : "View stats"}
          </button>
        </div>
        {data && (
          <>
            <div className="mb-6 rounded-xl border-2 border-[#FF0000] bg-white p-5" data-testid="today-glance">
              <div className="text-xs font-bold uppercase tracking-wider text-[#FF0000] mb-3">Today at a glance</div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><div className="text-3xl font-black">{data.visits_today ?? 0}</div><div className="text-xs text-[#606060] mt-1">site visits</div></div>
                <div><div className="text-3xl font-black">{data.visitors_today ?? 0}</div><div className="text-xs text-[#606060] mt-1">unique visitors</div></div>
                <div><div className="text-3xl font-black">{data.today}</div><div className="text-xs text-[#606060] mt-1">video checks</div></div>
              </div>
              <div className="mt-3 text-center text-[11px] text-[#909090]">All-time visits: {data.visits_total ?? 0} (visit tracking is new — traffic before it was enabled wasn't counted)</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatBox icon={TrendingUp} label="Total checks" value={data.total} />
              <StatBox icon={BarChart3} label="Checks today" value={data.today} />
              <StatBox icon={Users} label="Sign-ups" value={data.users} />
              <StatBox icon={Sparkles} label="Waitlist" value={data.waitlist} />
            </div>
            <div className="mt-6 rounded-xl border border-[#E5E5E5] bg-white p-5">
              <div className="text-sm font-bold mb-4">Checks per day</div>
              <div className="flex items-end gap-2 h-40">
                {data.daily.length === 0 && <p className="text-sm text-[#909090]">No data yet — share your link to start collecting!</p>}
                {data.daily.map((d) => (
                  <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-[#FF0000] rounded-t" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? 4 : 0 }} title={`${d.count}`}></div>
                    <span className="text-[9px] text-[#909090]">{d.day.slice(5)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-xs text-[#606060]">Anonymous: {data.anonymous} · Signed-in: {data.authed}</div>
            </div>
            <div className="mt-6 rounded-xl border border-[#E5E5E5] bg-white p-5">
              <div className="text-sm font-bold mb-3">Most-checked videos</div>
              {data.top_videos.length === 0 && <p className="text-sm text-[#909090]">No checks yet.</p>}
              {data.top_videos.map((v) => (
                <div key={v.video_id} className="flex items-center justify-between py-2 border-t border-[#F0F0F0] first:border-0 text-sm">
                  <span className="truncate flex-1 mr-3">{v.title || v.video_id}</span>
                  <span className="text-[#909090] mr-3">{v.verdict}</span>
                  <span className="font-bold">{v.count}×</span>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default function App() {
  const [url, setUrl] = useState("");
  const [saveHistory, setSaveHistory] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [user, setUser] = useState(null);
  const [paywall, setPaywall] = useState(null); // 'free_limit_reached' | 'needs_credits' | null
  const [quota, setQuota] = useState(null);
  const [buying, setBuying] = useState(false);
  const [share, setShare] = useState(null);
  const [sharedView, setSharedView] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptText, setTranscriptText] = useState("");
  const [extCode, setExtCode] = useState(null);
  const authProcessed = useRef(false);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get("/history");
      setHistory(res.data);
    } catch (e) { console.error("history load failed", e); }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  // Handle OAuth callback (#session_id=...) and Stripe return (?session_id=...)
  useEffect(() => {
    const run = async () => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const hash = window.location.hash || "";
      if (hash.includes("session_id=") && !authProcessed.current) {
        authProcessed.current = true;
        const sid = hash.split("session_id=")[1].split("&")[0];
        try {
          const res = await api.post("/auth/session", { session_id: sid });
          setUser(res.data);
          toast.success(`Welcome, ${res.data.name || "back"}!`);
          if (localStorage.getItem("fs_link_ext")) {
            localStorage.removeItem("fs_link_ext");
            requestExtCode();
          }
        } catch (e) {
          toast.error("Sign-in failed. Please try again.");
        }
        window.history.replaceState(null, "", window.location.pathname);
        loadHistory();
        return;
      }
      const me = await refreshMe();
      loadHistory();
      fetchQuota();
      api.post("/visit").catch(() => {});

      // Shared verdict link (?share=ID)
      const params = new URLSearchParams(window.location.search);
      if (params.get("link_ext")) {
        window.history.replaceState(null, "", window.location.pathname);
        if (me) {
          requestExtCode();
        } else {
          localStorage.setItem("fs_link_ext", "1");
          toast("Sign in to link your extension", { icon: "🔗" });
          login();
        }
      }
      if (params.get("buy")) {
        window.history.replaceState(null, "", window.location.pathname);
        setPaywall("free_limit_reached");
      }
      const shareId = params.get("share");
      if (shareId) {
        try {
          const res = await api.get(`/share/${shareId}`);
          setResult(res.data);
          setSharedView(true);
        } catch (e) {
          toast.error("That shared verdict could not be found.");
        }
      }

      // Stripe return
      const checkoutSid = params.get("session_id");
      if (checkoutSid) {
        window.history.replaceState(null, "", window.location.pathname);
        pollPayment(checkoutSid, 0);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPayment = async (sid, attempt) => {
    if (attempt === 0) toast.loading("Confirming your payment...", { id: "pay" });
    if (attempt >= 6) { toast.error("Payment status check timed out.", { id: "pay" }); return; }
    try {
      const res = await api.get(`/payments/status/${sid}`);
      if (res.data.payment_status === "paid") {
        await refreshMe();
        toast.success(`Payment successful! ${res.data.credits} credits available.`, { id: "pay" });
        setPaywall(null);
        setTimeout(() => {
          toast("Using the browser extension? Link it so your credits work there too.", {
            icon: "🧩",
            duration: 15000,
            action: { label: "Link extension", onClick: () => requestExtCode() },
          });
        }, 1500);
        return;
      }
      if (res.data.status === "expired") { toast.error("Payment session expired.", { id: "pay" }); return; }
      setTimeout(() => pollPayment(sid, attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => pollPayment(sid, attempt + 1), 2000);
    }
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("logout failed", e); }
    setUser(null);
    setHistory([]);
    fetchQuota();
    toast.success("Signed out");
  };

  const fetchQuota = async () => {
    try { const r = await api.get("/quota"); setQuota(r.data); } catch (e) { /* non-blocking */ }
  };

  const requestExtCode = async () => {
    try {
      const res = await api.post("/ext/code");
      setExtCode(res.data.code);
    } catch (e) {
      toast.error("Could not generate a link code — make sure you're signed in.");
    }
  };

  const buyCredits = async () => {
    if (!user) { login(); return; }
    setBuying(true);
    try {
      const res = await api.post("/payments/checkout", {
        package_id: "pack_100",
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBuying(false);
    }
  };

  const analyze = async () => {
    if (!url.trim()) { toast.error("Paste a YouTube URL first."); return; }
    setLoading(true); setResult(null); setSharedView(false);
    try {
      const payload = { url, save_history: saveHistory };
      if (transcriptText.trim()) {
        payload.page_text = transcriptText.trim();
        payload.force_refresh = true; // ensure the pasted transcript is actually used
      }
      const start = await api.post("/analyze/start", payload);
      const jobId = start.data.job_id;
      let data = null;
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const jr = await api.get(`/analyze/job/${jobId}`);
        if (jr.data.status === "done") { data = jr.data.result; break; }
        if (jr.data.status === "error") {
          const err = new Error(jr.data.error || "Analysis failed.");
          err.response = { status: jr.data.code || 502, data: { detail: jr.data.error } };
          throw err;
        }
      }
      if (!data) throw new Error("Analysis timed out. Please try again.");
      setResult(data);
      if (typeof data.credits_remaining === "number") {
        setUser((u) => (u ? { ...u, credits: data.credits_remaining } : u));
      }
      fetchQuota();
      if (saveHistory) loadHistory();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429) {
        toast.error("You've hit today's free limit on this network. Please try again tomorrow.");
      } else if (e.response?.status === 402 && (detail === "free_limit_reached" || detail === "needs_credits")) {
        setPaywall(detail);
        fetchQuota();
      } else {
        toast.error(detail || "Analysis failed. Try another video.");
      }
    } finally {
      setLoading(false);
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/history/${id}`);
    setHistory((h) => h.filter((x) => x.id !== id));
    toast.success("Removed from history");
  };

  const handleShare = async (r) => {
    try {
      const res = await api.post("/share", r);
      const link = `${API}/s/${res.data.id}`;
      setShare({ id: res.data.id, url: link, verdict: r.verdict, confidence: r.confidence });
    } catch (e) {
      toast.error("Could not create a share link.");
    }
  };

  const path = window.location.pathname;
  if (path === "/privacy") return <PrivacyPolicy />;
  if (path === "/stats") return <StatsDashboard />;

  return (
    <div className="App min-h-screen">
      <Toaster position="top-center" />
      {/* Header */}
      <header className="border-b border-[#E5E5E5] bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="bg-[#FF0000] text-white p-1.5 rounded-xl"><ShieldCheck size={22} /></div>
            <div>
              <div className="font-display font-black text-xl tracking-tight leading-none">FactScope</div>
              <div className="text-[10px] tracking-[0.15em] text-[#909090] uppercase">YouTube Misinformation Scanner</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <a href={EXT_CTA.url} target="_blank" rel="noopener noreferrer" data-testid="add-extension-link"
              className="hidden sm:flex items-center gap-1.5 border border-[#E5E5E5] hover:border-[#0F0F0F] px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
              <Puzzle size={15} /> {EXT_CTA.label}
            </a>
            {user ? (
              <>
                <div className="flex items-center gap-1.5 border border-[#E5E5E5] bg-[#F8F8F8] px-3 py-1.5 rounded-full whitespace-nowrap" data-testid="credits-badge">
                  <Coins size={15} className="text-[#FF0000]" />
                  <span className="text-sm font-bold">{user.credits}</span>
                  <span className="hidden sm:inline text-[11px] text-[#606060] font-medium">credits</span>
                </div>
                <button onClick={() => setPaywall("buy")} data-testid="buy-credits-button"
                  className="hidden sm:flex items-center gap-1.5 bg-[#FF0000] text-white px-4 py-1.5 text-sm font-medium rounded-full hover:bg-[#CC0000] transition-colors">
                  <Sparkles size={14} /> Buy
                </button>
                {user.picture
                  ? <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                  : <div className="w-9 h-9 rounded-full bg-[#0F0F0F] text-white flex items-center justify-center font-bold text-sm">{(user.name || user.email || "?")[0].toUpperCase()}</div>}
                <button onClick={logout} data-testid="logout-button" title="Sign out"
                  className="text-[#606060] hover:text-[#0F0F0F] transition-colors p-1"><LogOut size={18} /></button>
              </>
            ) : (
              <>
                {quota && !quota.logged_in && (quota.free_left > 0 ? (
                  <div className="flex items-center gap-1.5 border border-[#E5E5E5] bg-[#F8F8F8] px-3 py-1.5 rounded-full whitespace-nowrap" data-testid="free-checks-badge">
                    <Coins size={15} className={quota.free_left === 1 ? "text-[#B06000]" : "text-[#2BA640]"} />
                    <span className="text-sm font-bold">{quota.free_left}</span>
                    <span className="hidden sm:inline text-[11px] text-[#606060] font-medium">free {quota.free_left === 1 ? "check" : "checks"} left</span>
                  </div>
                ) : (
                  <button onClick={() => setPaywall("free_limit_reached")} data-testid="free-checks-buy-button"
                    className="flex items-center gap-1.5 bg-[#0F0F0F] text-white px-3 py-1.5 text-sm font-medium rounded-full hover:bg-[#2b2b2b] transition-colors whitespace-nowrap">
                    <Sparkles size={14} /> <span className="hidden sm:inline">Get 100 checks —</span> $5
                  </button>
                ))}
                <button onClick={login} data-testid="login-button"
                  className="flex items-center gap-2 bg-[#FF0000] text-white px-4 py-2 text-sm font-medium rounded-full hover:bg-[#CC0000] transition-colors">
                  <LogIn size={16} /> Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {extCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="ext-link-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center relative">
            <button onClick={() => setExtCode(null)} data-testid="ext-link-close" className="absolute top-4 right-4 text-[#606060] hover:text-black"><X size={20} /></button>
            <Puzzle size={32} className="mx-auto mb-3 text-[#FF0000]" />
            <div className="text-lg font-bold mb-2">Link your extension</div>
            <p className="text-sm text-[#606060] mb-4">In the FactScope panel on YouTube, click <strong>"Link your account"</strong> and type this code. It expires in 10 minutes.</p>
            <div className="text-4xl font-black tracking-[0.3em] bg-[#F8F8F8] border border-[#E5E5E5] rounded-xl py-4 mb-4 select-all" data-testid="ext-link-code">{extCode}</div>
            <button onClick={requestExtCode} className="text-sm text-[#065FD4] hover:underline" data-testid="ext-link-new-code">Generate a new code</button>
          </div>
        </div>
      )}
      {paywall && (
        <Paywall mode={paywall} user={user} onClose={() => setPaywall(null)} onBuy={buyCredits} buying={buying} />
      )}
      {share && <ShareModal share={share} onClose={() => setShare(null)} />}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 lg:py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: input + result */}
        <div className="lg:col-span-2 space-y-8">
          <section>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-[#0F0F0F] leading-[1.05]">
              Is this video<br />real, fake, or a story?
            </h1>
            <p className="mt-3 text-base text-[#606060] max-w-xl">
              Paste a YouTube link. The AI reads the transcript, extracts the claims, cross-checks them against live web sources, and returns a verdict.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <input
                data-testid="url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && analyze()}
                placeholder="https://www.youtube.com/watch?v=..."
                className="flex-1 h-12 px-5 border border-[#CCCCCC] bg-white rounded-full focus:border-[#065FD4] focus:ring-1 focus:ring-[#065FD4] focus:outline-none text-sm"
              />
              <button
                data-testid="analyze-button"
                onClick={analyze}
                disabled={loading}
                className="h-12 px-7 bg-[#FF0000] text-white font-medium rounded-full flex items-center justify-center gap-2 hover:bg-[#CC0000] transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                Analyze
              </button>
            </div>

            <label className="mt-4 flex items-center gap-3 cursor-pointer w-fit">
              <Switch checked={saveHistory} onCheckedChange={setSaveHistory} data-testid="save-history-toggle" />
              <span className="text-sm text-[#555]">Save this check to my history</span>
            </label>

            <div className="mt-4">
              {!showTranscript ? (
                <button onClick={() => setShowTranscript(true)} data-testid="add-transcript-toggle"
                  className="inline-flex items-center gap-1.5 text-sm text-[#065FD4] hover:underline font-medium">
                  <FileText size={15} /> Add the video's transcript for a more comprehensive result (optional)
                </button>
              ) : (
                <div data-testid="transcript-panel">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#333] flex items-center gap-1.5"><FileText size={15} className="text-[#065FD4]" /> Video transcript (optional)</span>
                    <button onClick={() => { setShowTranscript(false); setTranscriptText(""); }} className="text-xs text-[#909090] hover:text-[#0F0F0F]" data-testid="hide-transcript">Hide</button>
                  </div>
                  <p className="mt-1 text-[11px] text-[#909090] leading-relaxed">
                    On YouTube: click <strong>“…more”</strong> under the video → <strong>“Show transcript”</strong> → select all → copy → paste here. Doing this <em>before</em> you analyze gives a far more comprehensive verdict in a single check.
                  </p>
                  <textarea value={transcriptText} onChange={(e) => setTranscriptText(e.target.value)} rows={5} data-testid="transcript-input"
                    placeholder="Paste the video transcript here (optional)…"
                    className="mt-2 w-full rounded-xl border border-[#CCCCCC] bg-white p-3 text-xs focus:border-[#065FD4] focus:ring-1 focus:ring-[#065FD4] focus:outline-none" />
                </div>
              )}
            </div>

            {!user && (
              <p className="mt-3 font-mono-plex text-xs text-[#999]" data-testid="free-hint">
                2 free checks — no sign-up needed. After that, sign in &amp; grab credits.
              </p>
            )}
          </section>

          {loading && <LoadingState />}
          {!loading && result && <ResultPanel r={result} key={result.id} onShare={handleShare} shared={sharedView} />}
          {!loading && !result && (
            <div className="border border-dashed border-[#ccc] p-10 text-center" data-testid="empty-state">
              <Youtube size={40} className="mx-auto text-[#ccc]" />
              <p className="mt-3 font-mono-plex text-sm text-[#999]">No analysis yet. Paste a link above to begin.</p>
            </div>
          )}
        </div>

        {/* Right: history */}
        <aside className="lg:col-span-1">
          <div className="rounded-xl border border-[#E5E5E5] bg-white">
            <div className="flex items-center gap-2 border-b border-[#E5E5E5] px-4 py-3">
              <History size={16} />
              <span className="font-display text-sm font-bold uppercase tracking-[0.1em]">History</span>
              <span className="ml-auto font-mono-plex text-xs text-[#999]">{history.length}</span>
            </div>
            <div className="max-h-[70vh] overflow-y-auto custom-scrollbar" data-testid="history-list">
              {history.length === 0 && (
                <p className="p-4 font-mono-plex text-xs text-[#aaa]">No saved checks yet.</p>
              )}
              {history.map((h) => {
                const s = vstyle(h.verdict);
                return (
                  <button
                    key={h.id}
                    onClick={() => { setResult(h); setSharedView(false); }}
                    data-testid={`history-item-${h.id}`}
                    className="w-full text-left flex gap-3 px-4 py-3 border-b border-[#F0F0F0] hover:bg-[#FAFAFA] transition-colors group"
                  >
                    <span className="w-1 shrink-0" style={{ background: s.border }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-plex text-[10px] uppercase tracking-wider" style={{ color: s.text }}>
                        {h.verdict} · {h.confidence}%
                      </div>
                      <div className="text-sm text-[#0F0F0F] truncate">{h.title || h.video_url}</div>
                    </div>
                    <span onClick={(e) => del(h.id, e)} data-testid={`history-delete-${h.id}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[#999] hover:text-[#B71C1C]">
                      <Trash2 size={15} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <p className="mt-4 font-mono-plex text-[11px] leading-relaxed text-[#999]">
            Want this on every video? Get the browser extension — a one-click verdict right on the YouTube page:{" "}
            <a href={EXTENSION_URLS.chrome} target="_blank" rel="noopener noreferrer" className="text-[#0F0F0F] underline hover:text-[#FF0000]" data-testid="footer-chrome-link">Chrome</a>
            {" · "}
            <a href={EXTENSION_URLS.firefox} target="_blank" rel="noopener noreferrer" className="text-[#0F0F0F] underline hover:text-[#FF0000]" data-testid="footer-firefox-link">Firefox</a>
            {" · "}
            <a href={EXTENSION_URLS.chrome} target="_blank" rel="noopener noreferrer" className="text-[#0F0F0F] underline hover:text-[#FF0000]" data-testid="footer-edge-link">Edge</a>
          </p>
        </aside>
      </main>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-12 pt-4">
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-4" data-testid="disclaimer">
          <p className="text-xs leading-relaxed text-[#909090]">
            <span className="font-semibold text-[#606060]">Disclaimer:</span> FactScope uses AI and can be wrong. Verdicts are automated estimates generated from transcripts and web sources — they are not professional fact-checking, journalism, or legal advice. Always verify important claims yourself. FactScope and its operators accept no liability for any decisions, actions, or damages arising from reliance on these results. Not affiliated with or endorsed by YouTube or Google.
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <a href="/privacy" className="text-[#065FD4] hover:underline" data-testid="privacy-link">Privacy Policy</a>
            <a href="/stats" className="text-[#909090] hover:underline" data-testid="stats-link">Usage stats</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
